import React from 'react';
import styled, { css } from 'styled-components';
import { usePayment } from '../../shared/hooks/useApi/usePayment';
import { useModalStore } from '../../shared/store/portal/modalStore';
import { DeviceType, DividerType } from '../../shared/enum/EnumRepository';
import Divider from '../../shared/Divider';
import { useDeviceStore } from '../../shared/store/deviceStore';
import { getSettingsMessage } from '../../shared/constants/pageMessages';

/** `pageMessages`의 `\n` → 줄바꿈 */
const introWithLineBreaks = (raw: string) =>
  raw.split('\n').map((line, i, lines) => (
    <React.Fragment key={i}>
      {line}
      {i < lines.length - 1 ? <br /> : null}
    </React.Fragment>
  ));

const ProductCard: React.FC = () => {
  const { standardModal } = useModalStore();
  const { product } = usePayment();
  const { deviceType } = useDeviceStore();


  if (standardModal == null || !product) return null;

  const price = product.prices[0].price_amount / 100;
  const currencyConverter = () => {
    if (product.prices[0].price_currency === 'usd') {
      return '$'
    } else if (product.prices[0].price_currency === 'eur') {
      return '€'
    } else if (product.prices[0].price_currency === 'gbp') {
      return '£'
    }
  }

  const intervalLabel = () => {
    const { recurring_interval } = product.prices[0];
    if (recurring_interval === 'month') {
      return getSettingsMessage('SUBSCRIPTION_PRICE_INTERVAL_MONTH');
    }
    if (recurring_interval === 'year') {
      return getSettingsMessage('SUBSCRIPTION_PRICE_INTERVAL_YEAR');
    }
    return '';
  };

  return (
    <IntroduceComp>
      <PlanTitle>
        {product.name}
      </PlanTitle>
      <PlainImg src={product.medias[0].public_url} alt={product.name} />
      <Description $isMobile={deviceType === DeviceType.MOBILE}>
        <DescIntro>
          {introWithLineBreaks(getSettingsMessage('SUBSCRIPTION_PRODUCT_INTRO'))}
        </DescIntro>
        <DescBulletBlock>
          <DescDash>- {getSettingsMessage('SUBSCRIPTION_PRODUCT_FEATURE_STORAGE')}</DescDash>
          <DescDash>- {getSettingsMessage('SUBSCRIPTION_PRODUCT_FEATURE_UPLOADS')}</DescDash>
          <DescDash>- {getSettingsMessage('SUBSCRIPTION_PRODUCT_FEATURE_VISIBILITY')}</DescDash>
          <DescDash>- {getSettingsMessage('SUBSCRIPTION_PRODUCT_FEATURE_CANCEL')}</DescDash>
        </DescBulletBlock>
      </Description>
      <Divider dividerType={DividerType.PLAIN} bottom={"clamp(6px, 1vw, 10px)"} />
      <Price>{currencyConverter()} {price}
        <PriceUnit> /{intervalLabel()}</PriceUnit>
      </Price>
      <Divider dividerType={DividerType.PLAIN} bottom={"clamp(6px, 1vw, 15px)"} />
    </IntroduceComp>
  );
}

const IntroduceComp = styled.div`
  width: clamp(300px, 100%, 500px);
  height: 100%;
  flex-shrink: 0; // 추가: 크기 고정
  transition: none; // 내부 이동/크기 변화 없음
`;

const PlanTitle = styled.div`
  width: 100%;
  padding: 5px 0px;
  ${({ theme }) => theme.typography.Body_01_1};
  margin-bottom: clamp(6px, 1vw, 6px);
`;

const PlainImg = styled.img`
  width: 100%;
  height: clamp(160px, 48vw, 280px);
  object-fit: cover;
  border-radius: 0.5rem;
  margin-bottom: clamp(10px, 2vw, 10px);
`;

const descCopy = css`
  color: ${({ theme }) => theme.colors.color_Gray_04};
  ${({ theme }) => theme.typography.Body_04};
  font-weight: 400;
`;

const Description = styled.div<{ $isMobile: boolean }>`
  text-align: left;
  line-height: ${({ $isMobile }) => ($isMobile ? 1.45 : 1.5)};
  margin-bottom: clamp(18px, 3.5vw, 25px);
`;

const DescIntro = styled.div`
  margin: 0 0 1em 0;
  ${descCopy}
`;

const DescBulletBlock = styled.div`
  margin: 0;
`;

const DescDash = styled.p`
  margin: 0.12em 0 0 0;
  ${descCopy}

  &:first-child {
    margin-top: 0;
  }
`;

const Price = styled.div`
  padding: 4px 0px;
  margin-bottom: clamp(6px, 1vw, 10px);
  color: ${({ theme }) => theme.colors.color_Gray_01};
  ${({ theme }) => theme.typography.Body_02_1};
`;

const PriceUnit = styled.span`
  color: ${({ theme }) => theme.colors.color_Gray_04};
  ${({ theme }) => theme.typography.Body_04};
`;

export default ProductCard;